import React, { useEffect, useMemo, useState } from "react";
import {
  Col, Row, Card, CardBody, Badge, Button, Modal, ModalHeader, ModalBody,
  ModalFooter, Spinner, Input, Label, FormGroup, Alert
} from "reactstrap";
import TableContainer from "../../../components/Common/TableContainer";
import Breadcrumbs from "../../../components/Common/Breadcrumb";

const BASE_URL = "https://harmanappapi.stechomeyazilim.info";

const EMPTY_FORM = {
  ID: null,
  BASLIK: "",
  ACIKLAMA: "",
  VIDEO_URL: "",
  THUMBNAIL_URL: "",
  KATEGORI_ID: "",
  SURE: "",
  AKTIF: true,
  SIRA: 0,
};

const BilgiVideolari = () => {
  document.title = "Bilgi Videoları | HarmanaGEL";

  const authUser = JSON.parse(localStorage.getItem("authUser") || "{}");
  const token = localStorage.getItem("token") || authUser?.Token;
  const headers = { "Content-Type": "application/json", Authorization: `Bearer ${token}` };

  const [liste, setListe] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [successMsg, setSuccessMsg] = useState("");

  const [modal, setModal] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [formData, setFormData] = useState(EMPTY_FORM);

  const fetchListe = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const res = await fetch(`${BASE_URL}/BILGI_VIDEOLARI?$orderby=SIRA asc,ID desc`, { headers });
      const result = await res.json();
      setListe(result.value || []);
    } catch {
      setError("Bilgi videoları yüklenirken hata oluştu.");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => { fetchListe(); }, []);

  const openAdd = () => {
    setFormData(EMPTY_FORM);
    setIsEditing(false);
    setModal(true);
  };

  const handleEdit = (row) => {
    setFormData({
      ID: row.ID,
      BASLIK: row.BASLIK || "",
      ACIKLAMA: row.ACIKLAMA || "",
      VIDEO_URL: row.VIDEO_URL || "",
      THUMBNAIL_URL: row.THUMBNAIL_URL || "",
      KATEGORI_ID: row.KATEGORI_ID ?? "",
      SURE: row.SURE || "",
      AKTIF: row.AKTIF !== false,
      SIRA: row.SIRA ?? 0,
    });
    setIsEditing(true);
    setModal(true);
  };

  const handleSave = async () => {
    if (!formData.BASLIK.trim() || !formData.VIDEO_URL.trim()) {
      setError("Başlık ve video URL zorunludur.");
      return;
    }
    setIsSaving(true);
    try {
      const payload = {
        BASLIK: formData.BASLIK.trim(),
        ACIKLAMA: formData.ACIKLAMA || null,
        VIDEO_URL: formData.VIDEO_URL.trim(),
        THUMBNAIL_URL: formData.THUMBNAIL_URL || null,
        KATEGORI_ID: formData.KATEGORI_ID === "" ? null : Number(formData.KATEGORI_ID),
        SURE: formData.SURE || null,
        AKTIF: formData.AKTIF,
        SIRA: Number(formData.SIRA) || 0,
      };

      if (isEditing) {
        await fetch(`${BASE_URL}/BILGI_VIDEOLARI(${formData.ID})`, {
          method: "PATCH", headers, body: JSON.stringify(payload),
        });
        setSuccessMsg("Video güncellendi.");
      } else {
        await fetch(`${BASE_URL}/BILGI_VIDEOLARI`, {
          method: "POST", headers, body: JSON.stringify({ ...payload, IZLENME_SAYISI: 0, BEGENI_SAYISI: 0 }),
        });
        setSuccessMsg("Video eklendi.");
      }

      setModal(false);
      fetchListe();
      setTimeout(() => setSuccessMsg(""), 3000);
    } catch {
      setError("Kayıt sırasında hata oluştu.");
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Bu videoyu silmek istediğinize emin misiniz?")) return;
    try {
      await fetch(`${BASE_URL}/BILGI_VIDEOLARI(${id})`, { method: "DELETE", headers });
      setSuccessMsg("Video silindi.");
      fetchListe();
      setTimeout(() => setSuccessMsg(""), 3000);
    } catch {
      setError("Silme sırasında hata oluştu.");
    }
  };

  const columns = useMemo(() => [
    {
      header: "#",
      accessorKey: "ID",
      enableSorting: true,
      cell: (c) => <strong>#{c.getValue()}</strong>,
    },
    {
      header: "Sıra",
      accessorKey: "SIRA",
      enableSorting: true,
      cell: (c) => <Badge color="light" className="text-dark">{c.getValue() ?? 0}</Badge>,
    },
    {
      header: "Video",
      accessorKey: "BASLIK",
      enableSorting: true,
      cell: (c) => {
        const row = c.row.original;
        return (
          <div className="d-flex align-items-center gap-2">
            {row.THUMBNAIL_URL
              ? <img src={row.THUMBNAIL_URL} alt="" style={{ width: 56, height: 34, objectFit: "cover", borderRadius: 4 }} />
              : <span className="avatar-title rounded bg-light text-primary" style={{ width: 56, height: 34, display: "inline-flex", alignItems: "center", justifyContent: "center" }}><i className="bx bx-play" /></span>}
            <span className="fw-medium">{c.getValue() || "---"}</span>
          </div>
        );
      },
    },
    {
      header: "Süre",
      accessorKey: "SURE",
      enableSorting: false,
      cell: (c) => c.getValue() || <span className="text-muted">—</span>,
    },
    {
      header: "İzlenme / Beğeni",
      accessorKey: "IZLENME_SAYISI",
      enableSorting: true,
      cell: (c) => (
        <small className="text-muted">
          <i className="bx bx-show" /> {c.getValue() ?? 0} &nbsp; <i className="bx bx-like" /> {c.row.original.BEGENI_SAYISI ?? 0}
        </small>
      ),
    },
    {
      header: "Durum",
      accessorKey: "AKTIF",
      enableSorting: false,
      cell: (c) => <Badge color={c.getValue() ? "success" : "danger"}>{c.getValue() ? "Aktif" : "Pasif"}</Badge>,
    },
    {
      header: "İşlemler",
      enableSorting: false,
      cell: (cellProps) => {
        const row = cellProps.row.original;
        return (
          <div className="d-flex gap-2">
            <Button color="primary" size="sm" outline onClick={() => handleEdit(row)}>
              <i className="bx bx-edit" />
            </Button>
            <Button color="danger" size="sm" outline onClick={() => handleDelete(row.ID)}>
              <i className="bx bx-trash" />
            </Button>
          </div>
        );
      },
    },
  ], []);

  const f = (field, val) => setFormData(p => ({ ...p, [field]: val }));

  return (
    <React.Fragment>
      <div className="page-content">
        <div className="container-fluid">
          <Breadcrumbs title="İçerik Yönetimi" breadcrumbItem="Bilgi Videoları" />

          {successMsg && <Alert color="success">{successMsg}</Alert>}
          {error && <Alert color="danger" toggle={() => setError(null)}>{error}</Alert>}

          <Row>
            <Col lg={12}>
              <Card>
                <CardBody>
                  <div className="d-flex justify-content-between align-items-center mb-3">
                    <div>
                      <h4 className="card-title mb-1">Bilgi Videoları</h4>
                      <p className="text-muted small mb-0">
                        Mobil uygulamadaki eğitim / bilgilendirme videolarını buradan yönetin.
                      </p>
                    </div>
                    <Button color="primary" size="sm" onClick={openAdd}>
                      <i className="bx bx-plus me-1" />Yeni Video
                    </Button>
                  </div>

                  {isLoading ? (
                    <div className="text-center py-5"><Spinner color="primary" /></div>
                  ) : (
                    <TableContainer
                      columns={columns}
                      data={liste}
                      isGlobalFilter={true}
                      isPagination={true}
                      customPageSize={15}
                      SearchPlaceholder="Video ara..."
                      tableClass="align-middle table-nowrap table-hover"
                      theadClass="table-light"
                      paginationWrapper="dataTables_paginate paging_simple_numbers pagination-rounded"
                      pagination="pagination pagination-rounded justify-content-end mb-2"
                    />
                  )}
                </CardBody>
              </Card>
            </Col>
          </Row>
        </div>
      </div>

      <Modal isOpen={modal} toggle={() => setModal(false)} centered size="lg">
        <ModalHeader toggle={() => setModal(false)}>
          <i className={`mdi ${isEditing ? "mdi-video-outline" : "mdi-video-plus-outline"} me-2 text-primary`}></i>
          {isEditing ? `Video Düzenle (#${formData.ID})` : "Yeni Video Ekle"}
        </ModalHeader>
        <ModalBody>
          <Row>
            <Col md={12}>
              <FormGroup>
                <Label className="fw-bold">Başlık <span className="text-danger">*</span></Label>
                <Input value={formData.BASLIK} onChange={(e) => f("BASLIK", e.target.value)} placeholder="Video başlığı" maxLength={200} />
              </FormGroup>
            </Col>
            <Col md={12}>
              <FormGroup>
                <Label className="fw-bold">Açıklama</Label>
                <Input type="textarea" rows={3} value={formData.ACIKLAMA} onChange={(e) => f("ACIKLAMA", e.target.value)} placeholder="Açıklama" />
              </FormGroup>
            </Col>
            <Col md={12}>
              <FormGroup>
                <Label className="fw-bold">Video URL <span className="text-danger">*</span></Label>
                <Input value={formData.VIDEO_URL} onChange={(e) => f("VIDEO_URL", e.target.value)} placeholder="https://..." />
              </FormGroup>
            </Col>
            <Col md={12}>
              <FormGroup>
                <Label className="fw-bold">Thumbnail URL</Label>
                <Input value={formData.THUMBNAIL_URL} onChange={(e) => f("THUMBNAIL_URL", e.target.value)} placeholder="https://..." />
                {formData.THUMBNAIL_URL && <img src={formData.THUMBNAIL_URL} alt="önizleme" className="mt-2" style={{ maxHeight: 70, borderRadius: 6 }} />}
              </FormGroup>
            </Col>
            <Col md={4}>
              <FormGroup>
                <Label className="fw-bold">Kategori ID</Label>
                <Input type="number" value={formData.KATEGORI_ID} onChange={(e) => f("KATEGORI_ID", e.target.value)} placeholder="(isteğe bağlı)" />
              </FormGroup>
            </Col>
            <Col md={4}>
              <FormGroup>
                <Label className="fw-bold">Süre</Label>
                <Input value={formData.SURE} onChange={(e) => f("SURE", e.target.value)} placeholder="Örn: 03:45" maxLength={20} />
              </FormGroup>
            </Col>
            <Col md={4}>
              <FormGroup>
                <Label className="fw-bold">Sıra No</Label>
                <Input type="number" min="0" value={formData.SIRA} onChange={(e) => f("SIRA", e.target.value)} />
              </FormGroup>
            </Col>
            <Col md={12}>
              <FormGroup check className="mt-1">
                <Input type="checkbox" id="videoAktif" checked={formData.AKTIF} onChange={(e) => f("AKTIF", e.target.checked)} />
                <Label check for="videoAktif" className="fw-bold ms-1">Aktif — mobilde gösterilsin</Label>
              </FormGroup>
            </Col>
          </Row>
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" outline onClick={() => setModal(false)} disabled={isSaving}>İptal</Button>
          <Button color="primary" onClick={handleSave} disabled={isSaving}>
            {isSaving ? <Spinner size="sm" className="me-1" /> : <i className="mdi mdi-content-save me-1"></i>}
            {isEditing ? "Güncelle" : "Kaydet"}
          </Button>
        </ModalFooter>
      </Modal>
    </React.Fragment>
  );
};

export default BilgiVideolari;
